import type { Provider } from '@natashu/shared';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';
import { ProviderIcon } from './ProviderIcon.tsx';
import './Avatar.css';

const THAI = /[฀-๿]/;
const THAI_LEADING_VOWEL = /^[เ-ไ]/; // เ แ โ ใ ไ are written before the consonant
const segmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter('th', { granularity: 'grapheme' }) : null;

function firstGrapheme(word: string): string {
  if (segmenter) {
    for (const s of segmenter.segment(word)) return s.segment;
    return '';
  }
  return Array.from(word)[0] ?? '';
}

/** Thai names → first consonant ("สมชาย" → "ส", "ไพโรจน์" → "พ"); Latin → first + last initials. */
export function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').normalize('NFC').trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  if (!first) return '?';
  if (THAI.test(first.charAt(0))) return first.replace(THAI_LEADING_VOWEL, '').charAt(0) || first.charAt(0);
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  const a = firstGrapheme(first);
  const b = last && !THAI.test(last.charAt(0)) ? firstGrapheme(last) : '';
  return (a + b).toUpperCase();
}

export interface AvatarProps {
  name: string | null | undefined;
  src?: string | null;
  size?: number;
  /** Shows the channel mark in the badge slot. */
  provider?: Provider | null;
  /** Custom badge slot (overrides `provider`). */
  badge?: ReactNode;
  /** Decorative avatars (next to a visible name) are hidden from screen readers. */
  decorative?: boolean;
  className?: string;
}

export function Avatar({ name, src, size = 36, provider, badge, decorative = true, className }: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = Boolean(src) && failedSrc !== src;
  const badgeContent = badge ?? (provider ? <ProviderIcon provider={provider} size={Math.max(14, Math.round(size * 0.42))} demoTag={false} /> : null);
  return (
    <span
      className={cx('avatar', className)}
      style={{ '--avatar-size': `${size}px` } as CSSProperties}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : (name ?? undefined)}
      aria-hidden={decorative || undefined}
    >
      {showImage && src ? (
        <img src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setFailedSrc(src)} />
      ) : (
        <span className="avatar__initials">{initialsOf(name)}</span>
      )}
      {badgeContent && <span className="avatar__badge">{badgeContent}</span>}
    </span>
  );
}
